import Cookies from 'universal-cookie';
import { Suspense, useEffect } from 'react';
import { ApolloClient, ApolloProvider, InMemoryCache, HttpLink, ApolloLink } from '@apollo/client';
import { AppContextProvider, BehaviourContextProvider, ObjectContextProvider, UploadContextProvider } from './context/AppStore';
import Routes from './routes';
import { ToastContainer } from 'react-toastify';
import { onError } from 'apollo-link-error';
import { safeGetItem, safeSetItem, safeRemoveItem } from './utils/safeStorage';
import { COOKIE_VERSION, COOKIE_VERSION_KEY } from './utils/cookieVersion';

// Bump COOKIE_VERSION in utils/cookieVersion.ts when cookie semantics change to force
// every browser through a one-time server-side logout that clears HttpOnly cookies JS can't touch.
const COOKIE_VERSION_RETRY_KEY = `rafiki_cookie_version_retry_${COOKIE_VERSION}`;

let logoutInFlight = false;

const clearClientAuth = () => {
  safeRemoveItem('token');
  safeRemoveItem('userName');
  safeRemoveItem('restrictedToken');

  const cookies = new Cookies();
  cookies.remove('token', { path: '/' });
  cookies.remove('restrictedToken', { path: '/' });
  cookies.remove('googletoken', { path: '/' });
  cookies.remove('microsofttoken', { path: '/' });
  cookies.remove('salesforcetoken', { path: '/' });
};

const hardLogout = async (redirectToLogin = true) => {
  if (logoutInFlight) return;
  logoutInFlight = true;

  clearClientAuth();

  const results = await Promise.all([
    fetch(`${process.env.REACT_APP_REPOSITORY_URL}/logout`, {
      credentials: 'include',
    })
      .then((r) => r.ok)
      .catch(() => false),
    fetch(`${process.env.REACT_APP_EXTERNAL_URL}/users/logout`, {
      credentials: 'include',
    })
      .then((r) => r.ok)
      .catch(() => false),
  ]);

  if (results.every(Boolean)) {
    safeSetItem(COOKIE_VERSION_KEY, COOKIE_VERSION);
  }

  if (redirectToLogin) {
    window.location.replace('/#/login');
    return;
  }

  // Cookie migrations still clear both server-side and client-side auth, but
  // must preserve public routes such as shared meeting links.
  window.location.reload();
};

if (
  typeof window !== 'undefined' &&
  safeGetItem(COOKIE_VERSION_KEY) !== COOKIE_VERSION &&
  window.sessionStorage?.getItem(COOKIE_VERSION_RETRY_KEY) !== '1'
) {
  window.sessionStorage?.setItem(COOKIE_VERSION_RETRY_KEY, '1');
  hardLogout(false);
}

// Create an HTTP link that connects to your GraphQL endpoint
const httpLink = new HttpLink({
    uri: `${process.env.REACT_APP_REPOSITORY_URL}/graphql`,
    credentials: 'include', // include, same-origin, omit
});

// Set up the middleware to include the token in the authorization header for each request
/*const authLink = setContext((_, { headers }) => {
    const token = localStorage.getItem('token');
    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
        },
    };
});*/
const authLink = new ApolloLink((operation, forward) => {
    const token = safeGetItem('token');
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        Authorization:  token ? `Bearer ${token}` : '', // Replace with dynamic token retrieval
      },
    }));
    return forward(operation);
  });

  const refreshTokenLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const refreshedToken = operation
        .getContext()
        .response?.headers?.get('x-rafiki-token');

      if (refreshedToken) {
        safeSetItem('token', refreshedToken);
      }

      return response;
    });
  });

  const errorLink : ApolloLink | any = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, extensions }) => {
        console.error(`GraphQL Error: ${message}`);
  
        if (extensions?.code === "UNAUTHORIZED" || extensions?.code === "UNAUTHENTICATED") {
          hardLogout();
        }
      });
    }

    if (networkError) {
      console.error(`Network Error: ${networkError.message}`);

      const serverError = networkError as any;
      if (serverError.statusCode === 401) {
        hardLogout();
      }
    }
  });
  
  // 🔹 CHAIN LINKS IN CORRECT ORDER
  const client = new ApolloClient({
    link: ApolloLink.from([errorLink, authLink, refreshTokenLink, httpLink]), // Ensure errorLink comes first!
    cache: new InMemoryCache(),
    connectToDevTools: true,
  });

// The setAuthToken function now only needs to update the token in localStorage
export const setAuthToken = (token) => {
    safeSetItem('token', token);
};

// Set up global error listener for chunk loading errors with retry logic
window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('Loading chunk')) {
        console.warn('Chunk load error detected. Attempting to reload...');

        // Delay before reload to allow network recovery, etc.
        setTimeout(() => {
            window.location.reload();
        }, 3000); // 3-second delay to avoid immediate reload loops
    }
});

const loading = () => <div className="animated fadeIn pt-3 text-center">Loading...</div>;

const App = () => {
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (e.stopPropagation) {
                e.stopPropagation();
            } else if (window.event) {
                window.event.cancelBubble = true;
            }
        };

        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    return (
        <AppContextProvider meetingActiveTab={'1'}>
            <ObjectContextProvider>
                <BehaviourContextProvider>
                    <ApolloProvider client={client}>
                        <UploadContextProvider>
                            <Suspense fallback={loading()}>
                                <Routes />
                            </Suspense>
                            <ToastContainer
                                position="top-right"
                                hideProgressBar={false}
                                theme="light"
                                pauseOnHover
                                newestOnTop
                                autoClose={3000}
                            />
                        </UploadContextProvider>
                    </ApolloProvider>
                </BehaviourContextProvider>
            </ObjectContextProvider>
        </AppContextProvider>
    );
};

export default App;
